<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AltArrowRightIcon, MenuDotsIcon } from '@solar-icons/vue/linear'
import AppMenu from '../AppMenu/AppMenu.vue'
import type { MenuItem } from '../AppMenu/menu-types'
import type { BreadcrumbItem } from './breadcrumb-types'
import { collapseCrumbs, ELLIPSIS_CRUMB_ID } from './breadcrumb-collapse'

const props = withDefaults(
  defineProps<{
    /** The trail, root → current; the last item is the current location. */
    items: readonly BreadcrumbItem[]
    /** Ids of crumbs that accept a dragged item right now; a crumb outside this list ignores the drag. */
    dropTargetIds?: readonly string[]
    /** Element of the current crumb: `h1` when the trail itself carries the page heading. */
    currentAs?: 'span' | 'h1'
  }>(),
  {
    dropTargetIds: () => [],
    currentAs: 'span',
  },
)

const emit = defineEmits<{
  /** Fired when a navigable crumb is activated by click or keyboard. */
  navigate: [item: BreadcrumbItem]
  /** Fired when a dragged item is dropped on a crumb named by `dropTargetIds`. */
  itemDrop: [item: BreadcrumbItem]
}>()

const { t } = useI18n()

const overId = ref<string | null>(null)
const moreOpen = ref(false)

const trail = computed(() =>
  props.dropTargetIds.length > 0
    ? { visible: props.items, hidden: [] as readonly BreadcrumbItem[] }
    : collapseCrumbs(props.items),
)

const visibleItems = computed<readonly BreadcrumbItem[]>(() => trail.value.visible)

const hiddenMenuItems = computed<MenuItem[]>(() =>
  trail.value.hidden.map((item) => ({ id: item.id, label: item.label })),
)

const isCurrent = (index: number): boolean => index === visibleItems.value.length - 1

function onHiddenSelect(id: string): void {
  const item = trail.value.hidden.find((crumb) => crumb.id === id)
  if (item) emit('navigate', item)
}

const acceptsDrop = (item: BreadcrumbItem): boolean => props.dropTargetIds.includes(item.id)

function onDragOver(event: DragEvent, item: BreadcrumbItem): void {
  if (!acceptsDrop(item)) return
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  overId.value = item.id
}

function onDragLeave(item: BreadcrumbItem): void {
  if (overId.value === item.id) overId.value = null
}

function onDrop(event: DragEvent, item: BreadcrumbItem): void {
  if (!acceptsDrop(item)) return
  event.preventDefault()
  overId.value = null
  emit('itemDrop', item)
}
</script>

<template>
  <nav :aria-label="t('primitives.breadcrumb.ariaLabel')" class="icon-sm min-w-0">
    <ol class="flex min-w-0 items-center gap-2">
      <li
        v-for="(item, index) in visibleItems"
        :key="item.id"
        class="flex items-center gap-2"
        :class="isCurrent(index) ? 'min-w-0' : 'max-w-1/2 shrink-0'"
      >
        <AppMenu
          v-if="item.id === ELLIPSIS_CRUMB_ID"
          v-model:open="moreOpen"
          :items="hiddenMenuItems"
          placement="bottom-start"
          @select="onHiddenSelect"
        >
          <template #trigger="{ aria }">
            <button
              v-bind="aria"
              type="button"
              :aria-label="t('primitives.breadcrumb.moreAriaLabel')"
              class="grid shrink-0 cursor-pointer place-items-center rounded-1 text-text-tertiary transition-colors duration-fast ease-soft hover:text-text-primary focus-visible:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue outline-none motion-reduce:transition-none"
            >
              <MenuDotsIcon aria-hidden="true" />
            </button>
          </template>
        </AppMenu>

        <component
          :is="currentAs"
          v-else-if="isCurrent(index)"
          aria-current="page"
          :title="item.label"
          class="min-w-0 truncate text-body-sm-medium text-text-primary"
        >
          {{ item.label }}
        </component>

        <button
          v-else
          type="button"
          :class="[
            'min-w-0 cursor-pointer truncate rounded-1 text-body-sm transition-colors duration-fast ease-soft hover:text-text-primary focus-visible:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue outline-none motion-reduce:transition-none',
            overId === item.id
              ? 'bg-accent-blue/10 text-text-primary inset-ring-1 inset-ring-accent-blue'
              : 'text-text-tertiary',
          ]"
          @click="emit('navigate', item)"
          @dragover="onDragOver($event, item)"
          @dragleave="onDragLeave(item)"
          @drop="onDrop($event, item)"
        >
          {{ item.label }}
        </button>

        <AltArrowRightIcon
          v-if="!isCurrent(index)"
          aria-hidden="true"
          class="shrink-0 text-text-tertiary"
        />
      </li>
    </ol>
  </nav>
</template>
